import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { getEmployees, getSchools, createRecord, updateRecord, getRecord } from '../services/api';

const EMPTY_RECORD = {
  school_number: '', phone_number: '', school_address: '',
  serial_number: '', type: '', device_name: '', device_description: '',
  inventory_number: '', warranty_end_date: '', opened_date: '',
  opening_reason: '', opening_officer_id: '',
  inspection_officer_1_id: '', inspection_officer_2_id: '',
  motherboard_type: '', motherboard_serial: '',
  processor_type: '', processor_serial: '', processor_cores: '',
  core_speed_1: '', core_speed_2: '', has_cooling_fan: false,
  cd_type: '', cd_rom_speed: '', cd_write_speed: '',
  cd_dvd_combo_speed: '', dvd_writer_speed: '', cd_serial: '',
  fdd_type: '',
  sd_capacity: '', ddr1_capacity: '', ddr2_capacity: '', ddr3_capacity: '',
  has_at: false, has_atx_20pin: false, has_atx_24pin: false,
  casing_type: '', identified_issues: '', status: '',
  school_assigned_number: '', estimated_value: '', repair_comment: '',
};

const EMPTY_DEVICE = {
  device_name: '', serial_number: '', inventory_number: '',
  warranty_end_date: '', device_description: '',
  identified_issues: '', status: '',
  school_assigned_number: '', estimated_value: '', repair_comment: '',
};

const ic    = "mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";
const icErr = "mt-1 block w-full rounded-lg border border-red-400 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";
const lc    = "block text-sm font-medium text-gray-700 mb-1";

function safeDate(val) {
  if (!val) return '';
  const m = String(val).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

function Field({ label, required, error, children }) {
  return (
    <div data-error={error ? "true" : undefined}>
      <label className={lc}>
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
      <h3 className="text-base font-semibold text-gray-800 border-b border-gray-100 pb-3 flex items-center gap-2">
        <span className="w-1 h-5 bg-red-500 rounded-full inline-block"></span>
        {title}
      </h3>
      {children}
    </div>
  );
}

function SubTitle({ children }) {
  return <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider pt-2">{children}</p>;
}

const STATUS_OPTIONS = [
  ['fixed',       'මෙම උපකරණය සම්පූර්ණ වශයෙන් යථා තත්ත්වයට පත් කර ඇත'],
  ['needs_parts', 'අවශ්‍ය උපාංග මිලදී ගෙන සවිකිරීමෙන් යථා තත්ත්වයට පත් කළ හැකිය'],
  ['unusable',    'මෙම උපකරණය භාවිත කළ නොහැක'],
];

export default function MaintenanceFormPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = Boolean(id);

  const [schoolInput,    setSchoolInput]    = useState('');
  const [schoolName,     setSchoolName]     = useState('');
  const [schoolAddr,     setSchoolAddr]     = useState('');
  const [schoolPhone,    setSchoolPhone]    = useState('');
  const [schoolNotFound, setSchoolNotFound] = useState(false);

  const schoolNameRef  = useRef('');
  const schoolAddrRef  = useRef('');
  const schoolPhoneRef = useRef('');

  const [shared, setShared] = useState({
    opened_date: '', opening_reason: '', opening_officer_id: '',
    inspection_officer_1_id: '', inspection_officer_2_id: '', type: '',
  });
  const sharedRef = useRef({
    opened_date: '', opening_reason: '', opening_officer_id: '',
    inspection_officer_1_id: '', inspection_officer_2_id: '', type: '',
  });

  const [sysForm,  setSysForm]  = useState({ ...EMPTY_RECORD });
  const sysFormRef = useRef({ ...EMPTY_RECORD });

  const [devices,  setDevices]  = useState([{ ...EMPTY_DEVICE }]);
  const devicesRef = useRef([{ ...EMPTY_DEVICE }]);

  const [employees, setEmployees] = useState([]);
  const [schools,   setSchools]   = useState([]);
  const [errors,    setErrors]    = useState({});
  const [saving,    setSaving]    = useState(false);
  const [flashMsg,  setFlashMsg]  = useState('');
  const [loading,   setLoading]   = useState(isEdit);

  const isSysUnit = shared.type === 'system_unit';

  const applySchool = (school) => {
    const name  = school.name      || '';
    const addr  = school.address   || '';
    const phone = school.telephone || '';
    const sid   = school.school_id || '';
    schoolNameRef.current  = name;
    schoolAddrRef.current  = addr;
    schoolPhoneRef.current = phone;
    setSchoolInput(sid);
    setSchoolName(name);
    setSchoolAddr(addr);
    setSchoolPhone(phone);
    setSchoolNotFound(false);
    setErrors(p => ({ ...p, school_number: '', school_address: '' }));
  };

  useEffect(() => {
    getEmployees().then(res => setEmployees(res.data)).catch(() => {});

    getSchools().then(schoolsRes => {
      const list = schoolsRes.data;
      setSchools(list);

      if (isEdit) {
        getRecord(id).then(res => {
          const data = res.data;
          const type = data.type || '';

          // ── School ──
          const found =
            list.find(s => s.name      === data.school_number) ||
            list.find(s => s.school_id === data.school_number);

          if (found) {
            applySchool(found);
          } else {
            const name  = data.school_number  || '';
            const addr  = data.school_address || '';
            const phone = data.phone_number   || '';
            schoolNameRef.current  = name;
            schoolAddrRef.current  = addr;
            schoolPhoneRef.current = phone;
            setSchoolInput(name);
            setSchoolName(name);
            setSchoolAddr(addr);
            setSchoolPhone(phone);
          }

          // ── Shared ──
          const newShared = {
            opened_date:             safeDate(data.opened_date),
            opening_reason:          data.opening_reason                  || '',
            opening_officer_id:      String(data.opening_officer_id      || ''),
            inspection_officer_1_id: String(data.inspection_officer_1_id || ''),
            inspection_officer_2_id: String(data.inspection_officer_2_id || ''),
            type:                    type,
          };
          sharedRef.current = newShared;

          if (type === 'system_unit') {
            const cleaned = {};
            Object.keys(EMPTY_RECORD).forEach(k => {
              if (typeof EMPTY_RECORD[k] === 'boolean') {
                cleaned[k] = data[k] === true || data[k] === 1;
              } else if (k === 'warranty_end_date') {
                cleaned[k] = safeDate(data[k]);
              } else {
                cleaned[k] = data[k] !== null && data[k] !== undefined ? String(data[k]) : '';
              }
            });
            sysFormRef.current = cleaned;
            // flushSync: set sysForm and shared atomically
            flushSync(() => {
              setSysForm(cleaned);
              setShared(newShared);
            });
          } else {
            // Non system_unit device
            const deviceData = {
              device_name:            data.device_name            || '',
              serial_number:          data.serial_number          || '',
              inventory_number:       data.inventory_number       || '',
              warranty_end_date:      safeDate(data.warranty_end_date),
              device_description:     data.device_description     || '',
              identified_issues:      data.identified_issues      || '',
              status:                 data.status                 || '',
              school_assigned_number: data.school_assigned_number || '',
              estimated_value:        data.estimated_value !== null && data.estimated_value !== undefined
                                        ? String(data.estimated_value) : '',
              repair_comment:         data.repair_comment         || '',
            };
            devicesRef.current = [deviceData];
            // flushSync: force devices to render BEFORE shared.type is set
            flushSync(() => {
              setDevices([deviceData]);
            });
            // Now set shared — section renders with devices already in state
            setShared(newShared);
          }

          setLoading(false);
        }).catch(() => {
          alert('වාර්තාව සොයා ගත නොහැකි විය');
          setLoading(false);
        });
      }
    }).catch(() => {});
  }, [id, isEdit]);

  const handleSchoolIdInput = e => {
    const raw = e.target.value;
    const val = raw.trim();
    setSchoolInput(raw);
    schoolNameRef.current  = '';
    schoolAddrRef.current  = '';
    schoolPhoneRef.current = '';
    setSchoolName('');
    setSchoolAddr('');
    setSchoolPhone('');
    setSchoolNotFound(false);
    setErrors(p => ({ ...p, school_number: '' }));
    if (!val) return;
    const byId = schools.find(s => s.school_id?.toLowerCase() === val.toLowerCase());
    if (byId)   { applySchool(byId);   return; }
    const byName = schools.find(s => s.name === val);
    if (byName) { applySchool(byName); return; }
    if (val.length >= 3) setSchoolNotFound(true);
  };

  const setS = field => e => {
    const val = e.target.value;
    setShared(p => {
      const next = { ...p, [field]: val };
      sharedRef.current = next;
      return next;
    });
    setErrors(p => ({ ...p, [field]: '' }));
  };

  const setSys = field => e => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSysForm(p => {
      const next = { ...p, [field]: val };
      sysFormRef.current = next;
      return next;
    });
    setErrors(p => ({ ...p, [field]: '' }));
  };

  const setDevice = (index, field) => e => {
    const val = e.target.value;
    setDevices(prev => {
      const next = prev.map((d, i) => i === index ? { ...d, [field]: val } : d);
      devicesRef.current = next;
      return next;
    });
  };

  const addDevice = () => setDevices(p => {
    const next = [...p, { ...EMPTY_DEVICE }];
    devicesRef.current = next;
    return next;
  });

  const removeDevice = i => setDevices(p => {
    const next = p.filter((_, idx) => idx !== i);
    devicesRef.current = next;
    return next;
  });

  const handleSubmit = async e => {
    e.preventDefault();
    e.stopPropagation();

    const curSchoolName  = schoolNameRef.current;
    const curSchoolAddr  = schoolAddrRef.current;
    const curSchoolPhone = schoolPhoneRef.current;
    const curShared      = sharedRef.current;
    const curSysForm     = sysFormRef.current;
    const curDevices     = devicesRef.current;
    const curIsSysUnit   = curShared.type === 'system_unit';

    const errs = {};
    if (!curSchoolName.trim())                        errs.school_number           = 'වලංගු School ID ඇතුළත් කරන්න';
    if (!curSchoolAddr.trim())                        errs.school_address          = 'ලිපිනය අවශ්‍යයි';
    if (!curShared.type)                              errs.type                    = 'වර්ගය තෝරන්න';
    if (!curShared.opened_date)                       errs.opened_date             = 'විවෘත දිනය අවශ්‍යයි';
    if (!curShared.opening_reason?.trim())            errs.opening_reason          = 'හේතුව අවශ්‍යයි';
    if (!curShared.opening_officer_id)                errs.opening_officer_id      = 'නිලධාරියා තෝරන්න';
    if (!curShared.inspection_officer_1_id)           errs.inspection_officer_1_id = 'නිලධාරී 1 තෝරන්න';
    if (curIsSysUnit) {
      if (!curSysForm.identified_issues?.trim())      errs.identified_issues       = 'දෝෂ ඇතුළත් කරන්න';
      if (!curSysForm.status)                         errs.status                  = 'තත්ත්වය තෝරන්න';
      if (!curSysForm.school_assigned_number?.trim()) errs.school_assigned_number  = 'අංකය අවශ්‍යයි';
    } else {
      curDevices.forEach((d, i) => {
        if (!d.status)                         errs[`status_${i}`]                 = 'තත්ත්වය තෝරන්න';
        if (!d.school_assigned_number?.trim()) errs[`school_assigned_number_${i}`] = 'අංකය අවශ්‍යයි';
      });
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setFlashMsg('');
      setTimeout(() => {
        const el = document.querySelector('[data-error="true"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        else window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
      return;
    }

    setSaving(true);
    setFlashMsg('');

    try {
      const sharedData = {
        school_number:           curSchoolName.trim(),
        school_address:          curSchoolAddr.trim(),
        phone_number:            curSchoolPhone.trim(),
        opened_date:             curShared.opened_date,
        opening_reason:          curShared.opening_reason.trim(),
        opening_officer_id:      curShared.opening_officer_id,
        inspection_officer_1_id: curShared.inspection_officer_1_id,
        inspection_officer_2_id: curShared.inspection_officer_2_id || null,
        type:                    curShared.type,
      };

      if (curIsSysUnit) {
        const payload = { ...EMPTY_RECORD, ...curSysForm, ...sharedData };
        if (isEdit) await updateRecord(id, payload);
        else        await createRecord(payload);
      } else {
        for (const device of curDevices) {
          const payload = { ...EMPTY_RECORD, ...device, ...sharedData };
          if (isEdit) await updateRecord(id, payload);
          else        await createRecord(payload);
        }
      }

      setFlashMsg('✅ වාර්තාව සාර්ථකව සුරකින ලදී!');
      setTimeout(() => navigate('/maintenance'), 1500);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Unknown error';
      setFlashMsg('❌ දෝෂයක් ඇති විය: ' + msg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const empOptions = employees.map(emp => (
    <option key={emp.id} value={emp.id}>{emp.nama} — {emp.thanathura}</option>
  ));

  const schoolSuggestions = schoolInput.length >= 1 && !schoolName
    ? schools.filter(s =>
        s.school_id?.toLowerCase().includes(schoolInput.toLowerCase()) ||
        s.name?.toLowerCase().includes(schoolInput.toLowerCase())
      ).slice(0, 8)
    : [];

  const hasErrors = Object.keys(errors).length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-gray-400 text-sm">වාර්තාව පූරණය වෙමින්...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'නඩත්තු වාර්තාව සංස්කරණය' : 'නව නඩත්තු වාර්තාව'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">* අනිවාර්ය පිරවිය යුතු ක්ෂේත්‍ර</p>
        </div>
        <button type="button" onClick={() => navigate('/maintenance')}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 text-sm transition">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          ආපසු
        </button>
      </div>

      {flashMsg && (
        <div className={`mb-6 p-4 rounded-xl text-sm font-medium border ${
          flashMsg.startsWith('✅') ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
        }`}>{flashMsg}</div>
      )}

      {hasErrors && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200">
          <p className="text-sm font-semibold text-red-700 mb-2">⚠️ පහත ක්ෂේත්‍ර පිරවිය යුතුය:</p>
          <ul className="list-disc list-inside space-y-1">
            {errors.school_number           && <li className="text-xs text-red-600">පාසල් ID — {errors.school_number}</li>}
            {errors.school_address          && <li className="text-xs text-red-600">ලිපිනය — {errors.school_address}</li>}
            {errors.type                    && <li className="text-xs text-red-600">උපකරණ වර්ගය — {errors.type}</li>}
            {errors.opened_date             && <li className="text-xs text-red-600">විවෘත දිනය — {errors.opened_date}</li>}
            {errors.opening_reason          && <li className="text-xs text-red-600">විවෘත කිරීමේ හේතුව — {errors.opening_reason}</li>}
            {errors.opening_officer_id      && <li className="text-xs text-red-600">විවෘත කළ නිලධාරියා — {errors.opening_officer_id}</li>}
            {errors.inspection_officer_1_id && <li className="text-xs text-red-600">පරීක්ෂා නිලධාරී 1 — {errors.inspection_officer_1_id}</li>}
            {errors.identified_issues       && <li className="text-xs text-red-600">දෝෂ — {errors.identified_issues}</li>}
            {errors.status                  && <li className="text-xs text-red-600">තත්ත්වය — {errors.status}</li>}
            {errors.school_assigned_number  && <li className="text-xs text-red-600">පාසල ලබාදුන් අංකය — {errors.school_assigned_number}</li>}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-6">

        {/* ══ 1. School ══ */}
        <Section title="පාසල් තොරතුරු">
          <Field label="පාසල් ID" required error={errors.school_number}>
            <div className="relative">
              <input type="text" value={schoolInput} onChange={handleSchoolIdInput}
                placeholder="School ID ටයිප් කරන්න (eg: GS/001)..."
                className={errors.school_number ? icErr : ic} autoComplete="off" />
              {schoolSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {schoolSuggestions.map(s => (
                    <button key={s.id} type="button" onClick={() => applySchool(s)}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-red-50 hover:text-red-600 border-b border-gray-50 last:border-0">
                      <span className="font-semibold text-red-500">{s.school_id}</span>
                      <span className="text-gray-500 ml-2">— {s.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {schoolNotFound && (
              <p className="mt-1 text-xs text-orange-600">
                ⚠️ මෙම School ID සඳහා ගැලපෙන පාසලක් නොමැත.
                <a href="/schools" className="underline ml-1">පාසල් එකතු කරන්න →</a>
              </p>
            )}
          </Field>

          {schoolName && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
              <span className="text-green-600 text-sm font-semibold">✅ පාසල හඳුනාගන්නා ලදී</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">පාසල් නම</p>
                  <p className="text-sm font-semibold text-gray-800">{schoolName}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">දුරකථන අංකය</label>
                  <input type="text" value={schoolPhone}
                    onChange={e => { schoolPhoneRef.current = e.target.value; setSchoolPhone(e.target.value); }}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-red-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">
                  ලිපිනය {errors.school_address && <span className="text-red-500">— {errors.school_address}</span>}
                </label>
                <textarea rows={2} value={schoolAddr}
                  onChange={e => { schoolAddrRef.current = e.target.value; setSchoolAddr(e.target.value); setErrors(p => ({ ...p, school_address: '' })); }}
                  className={`block w-full rounded-lg border px-3 py-1.5 text-sm focus:outline-none ${errors.school_address ? 'border-red-400' : 'border-gray-300'}`} />
              </div>
            </div>
          )}

          {isEdit && !schoolName && schoolInput && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-3">
              <Field label="දුරකථන අංකය">
                <input type="text" value={schoolPhone}
                  onChange={e => { schoolPhoneRef.current = e.target.value; setSchoolPhone(e.target.value); }}
                  className={ic} />
              </Field>
              <Field label="පාසලේ ලිපිනය" required error={errors.school_address}>
                <textarea rows={2} value={schoolAddr}
                  onChange={e => { schoolAddrRef.current = e.target.value; setSchoolAddr(e.target.value); setErrors(p => ({ ...p, school_address: '' })); }}
                  className={errors.school_address ? icErr : ic} />
              </Field>
            </div>
          )}
        </Section>

        {/* ══ 2. Device Type ══ */}
        <Section title="උපකරණ වර්ගය">
          <Field label="වර්ගය" required error={errors.type}>
            <select value={shared.type} onChange={setS('type')} className={errors.type ? icErr : ic}>
              <option value="">-- තෝරන්න --</option>
              <option value="system_unit">💻 System Unit (එකක් පමණි)</option>
              <option value="monitor">🖥️ Monitor (බහු උපකරණ)</option>
              <option value="printer">🖨️ Printer (බහු උපකරණ)</option>
              <option value="ups">🔋 UPS (බහු උපකරණ)</option>
              <option value="other">📦 Other Device (බහු උපකරණ)</option>
            </select>
          </Field>
          {shared.type && (
            <div className={`p-3 rounded-lg text-sm border ${isSysUnit ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
              {isSysUnit ? '💻 System Unit: එකවර උපකරණ 1ක් ඇතුළත් කළ හැකිය.' : '📦 + බොත්තම ඔබා එකවර උපකරණ කිහිපයක් ඇතුළත් කළ හැකිය.'}
            </div>
          )}
        </Section>

        {/* ══ 3. System Unit ══ */}
        {isSysUnit && (
          <>
            <Section title="උපකරණ තොරතුරු">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="උපකරණයේ නම"><input type="text" value={sysForm.device_name} onChange={setSys('device_name')} className={ic} /></Field>
                <Field label="Serial Number"><input type="text" value={sysForm.serial_number} onChange={setSys('serial_number')} className={ic} /></Field>
                <Field label="Inventory Number"><input type="text" value={sysForm.inventory_number} onChange={setSys('inventory_number')} className={ic} /></Field>
                <Field label="වගකීම් අවසන් දිනය"><input type="date" value={sysForm.warranty_end_date} onChange={setSys('warranty_end_date')} className={ic} /></Field>
                <Field label="උපකරණ විස්තරය"><input type="text" value={sysForm.device_description} onChange={setSys('device_description')} className={ic} /></Field>
              </div>
            </Section>

            <Section title="System Unit විස්තර">
              <SubTitle>මව්පුවරුව (Motherboard)</SubTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="වර්ගය"><input type="text" value={sysForm.motherboard_type} onChange={setSys('motherboard_type')} className={ic} /></Field>
                <Field label="Serial Number"><input type="text" value={sysForm.motherboard_serial} onChange={setSys('motherboard_serial')} className={ic} /></Field>
              </div>
              <SubTitle>CPU</SubTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="වර්ගය"><input type="text" value={sysForm.processor_type} onChange={setSys('processor_type')} className={ic} /></Field>
                <Field label="Serial Number"><input type="text" value={sysForm.processor_serial} onChange={setSys('processor_serial')} className={ic} /></Field>
                <Field label="Cores"><input type="number" min="1" value={sysForm.processor_cores} onChange={setSys('processor_cores')} className={ic} /></Field>
                <Field label="Core Speed 1"><input type="text" value={sysForm.core_speed_1} onChange={setSys('core_speed_1')} className={ic} placeholder="eg: 3.2 GHz" /></Field>
                <Field label="Core Speed 2"><input type="text" value={sysForm.core_speed_2} onChange={setSys('core_speed_2')} className={ic} /></Field>
                <div className="flex items-center gap-3 mt-2">
                  <input id="cooling" type="checkbox"
                    checked={sysForm.has_cooling_fan === true || sysForm.has_cooling_fan === 'true' || sysForm.has_cooling_fan === 1}
                    onChange={setSys('has_cooling_fan')}
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500" />
                  <label htmlFor="cooling" className="text-sm text-gray-700 cursor-pointer">Cooling Fan සවි කර ඇත</label>
                </div>
              </div>
              <SubTitle>CD / DVD Drive</SubTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {[['cd_type','වර්ගය'],['cd_rom_speed','CD Rom Speed'],['cd_write_speed','CD Write Speed'],
                  ['cd_dvd_combo_speed','CD/DVD Combo Speed'],['dvd_writer_speed','DVD Writer Speed'],['cd_serial','Serial Number']
                ].map(([f,l]) => <Field key={f} label={l}><input type="text" value={sysForm[f]} onChange={setSys(f)} className={ic} /></Field>)}
              </div>
              <SubTitle>Floppy Disk Drive</SubTitle>
              <Field label="FDD වර්ගය"><input type="text" value={sysForm.fdd_type} onChange={setSys('fdd_type')} className={ic} /></Field>
              <SubTitle>RAM</SubTitle>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[['sd_capacity','SD'],['ddr1_capacity','DDR1'],['ddr2_capacity','DDR2'],['ddr3_capacity','DDR3']].map(([f,l]) => (
                  <Field key={f} label={`${l} Capacity`}><input type="text" value={sysForm[f]} onChange={setSys(f)} className={ic} placeholder="eg: 512MB" /></Field>
                ))}
              </div>
              <SubTitle>Power Supply Unit</SubTitle>
              <div className="flex flex-wrap gap-6">
                {[['has_at','AT'],['has_atx_20pin','ATX 20-pin'],['has_atx_24pin','ATX 24-pin']].map(([f,l]) => (
                  <label key={f} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox"
                      checked={sysForm[f] === true || sysForm[f] === 'true' || sysForm[f] === 1}
                      onChange={setSys(f)}
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500" />
                    <span className="text-sm text-gray-700">{l}</span>
                  </label>
                ))}
              </div>
              <SubTitle>Casing</SubTitle>
              <Field label="Casing Type">
                <select value={sysForm.casing_type} onChange={setSys('casing_type')} className={ic}>
                  <option value="">-- තෝරන්න --</option>
                  <option value="desktop">Desktop</option>
                  <option value="mini_tower">Mini Tower</option>
                  <option value="full_tower">Full Tower</option>
                </select>
              </Field>
            </Section>

            <Section title="ගැටළු සහ තත්ත්වය">
              <Field label="හඳුනාගත් දෝෂ / ගැටළු" required error={errors.identified_issues}>
                <textarea rows={4} value={sysForm.identified_issues} onChange={setSys('identified_issues')}
                  className={errors.identified_issues ? icErr : ic} placeholder="හඳුනාගත් දෝෂ/ගැටළු ඇතුළත් කරන්න" />
              </Field>
              <div>
                <p className={lc}>අවසන් වාර්තාව <span className="text-red-500">*</span></p>
                <div className="space-y-3 mt-2">
                  {STATUS_OPTIONS.map(([val, label]) => (
                    <label key={val} className="flex items-start gap-3 cursor-pointer">
                      <input type="radio" name="sys_status" value={val}
                        checked={sysForm.status === val}
                        onChange={setSys('status')}
                        className="mt-0.5 h-4 w-4 text-red-600 border-gray-300 focus:ring-red-500" />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
                {errors.status && <p className="mt-2 text-xs text-red-600">{errors.status}</p>}
              </div>
            </Section>

            <Section title="අතිරේක තොරතුරු">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="පාසල ලබාදුන් අංකය" required error={errors.school_assigned_number}>
                  <input type="text" value={sysForm.school_assigned_number} onChange={setSys('school_assigned_number')}
                    className={errors.school_assigned_number ? icErr : ic} />
                </Field>
                <Field label="තක්සේරු මිල (රු.)">
                  <input type="number" step="0.01" min="0" value={sysForm.estimated_value}
                    onChange={setSys('estimated_value')} className={ic} placeholder="0.00" />
                </Field>
              </div>
              <Field label="අමතර සටහන් / අවශ්‍ය කොටස්">
                <textarea rows={3} value={sysForm.repair_comment} onChange={setSys('repair_comment')} className={ic}
                  placeholder="අවශ්‍ය කොටස් ඇත්නම් එක් රේඛාවකට එකක් ලෙස ඇතුළත් කරන්න" />
              </Field>
            </Section>
          </>
        )}

        {/* ══ 4. Multi-device ══ */}
        {shared.type && !isSysUnit && (
          <Section title="උපකරණ ලැයිස්තුව">
            <div className="space-y-4">
              {devices.map((device, index) => (
                <div key={index} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-semibold text-red-600 bg-red-50 px-3 py-1 rounded-full">
                      උපකරණය #{index + 1}
                    </span>
                    {!isEdit && devices.length > 1 && (
                      <button type="button" onClick={() => removeDevice(index)}
                        className="text-red-400 hover:text-red-600 text-xs font-medium flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                        ඉවත් කරන්න
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="උපකරණයේ නම">
                      <input type="text" value={device.device_name} onChange={setDevice(index,'device_name')} className={ic} />
                    </Field>
                    <Field label="Serial Number">
                      <input type="text" value={device.serial_number} onChange={setDevice(index,'serial_number')} className={ic} />
                    </Field>
                    <Field label="Inventory Number">
                      <input type="text" value={device.inventory_number} onChange={setDevice(index,'inventory_number')} className={ic} />
                    </Field>
                    <Field label="වගකීම් අවසන් දිනය">
                      <input type="date" value={device.warranty_end_date} onChange={setDevice(index,'warranty_end_date')} className={ic} />
                    </Field>
                    <Field label="උපකරණ විස්තරය">
                      <input type="text" value={device.device_description} onChange={setDevice(index,'device_description')} className={ic} />
                    </Field>
                    <Field label="පාසල ලබාදුන් අංකය" required error={errors[`school_assigned_number_${index}`]}>
                      <input type="text" value={device.school_assigned_number} onChange={setDevice(index,'school_assigned_number')} className={ic} />
                    </Field>
                    <Field label="තක්සේරු මිල (රු.)">
                      <input type="number" step="0.01" min="0" value={device.estimated_value}
                        onChange={setDevice(index,'estimated_value')} className={ic} placeholder="0.00" />
                    </Field>
                  </div>
                  <div className="mt-4">
                    <Field label="හඳුනාගත් දෝෂ / ගැටළු">
                      <textarea rows={3} value={device.identified_issues} onChange={setDevice(index,'identified_issues')} className={ic} />
                    </Field>
                  </div>
                  <div className="mt-4">
                    <Field label="අමතර සටහන් / අවශ්‍ය කොටස්">
                      <textarea rows={2} value={device.repair_comment} onChange={setDevice(index,'repair_comment')} className={ic} />
                    </Field>
                  </div>
                  <div className="mt-4">
                    <p className={lc}>තත්ත්වය <span className="text-red-500">*</span></p>
                    <div className="flex flex-wrap gap-4 mt-2">
                      {[['fixed','යථා තත්ත්වයට පත්කර ඇත'],['needs_parts','අමතර කොටස් අවශ්‍ය වේ'],['unusable','භාවිත කළ නොහැක']].map(([val, label]) => (
                        <label key={val} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name={`status_${index}`} value={val}
                            checked={device.status === val}
                            onChange={setDevice(index,'status')}
                            className="h-4 w-4 text-red-600 border-gray-300" />
                          <span className="text-sm text-gray-700">{label}</span>
                        </label>
                      ))}
                    </div>
                    {errors[`status_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`status_${index}`]}</p>}
                  </div>
                </div>
              ))}
              {!isEdit && (
                <button type="button" onClick={addDevice}
                  className="w-full border-2 border-dashed border-red-300 hover:border-red-400 text-red-500 rounded-xl py-3 text-sm font-medium transition flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                  </svg>
                  තවත් උපකරණයක් එකතු කරන්න
                </button>
              )}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                📌 සියලු උපකරණ {devices.length}ක් එකවර submit වේ.
              </div>
            </div>
          </Section>
        )}

        {/* ══ 5. Inspection ══ */}
        {shared.type && (
          <Section title="පරීක්ෂා කිරීමේ තොරතුරු">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="විවෘත කළ දිනය" required error={errors.opened_date}>
                <input type="date" value={shared.opened_date} onChange={setS('opened_date')}
                  className={errors.opened_date ? icErr : ic} />
              </Field>
            </div>
            <Field label="විවෘත කිරීමට හේතුව" required error={errors.opening_reason}>
              <textarea rows={3} value={shared.opening_reason} onChange={setS('opening_reason')}
                className={errors.opening_reason ? icErr : ic} />
            </Field>
            <Field label="විවෘත කළ නිලධාරියා" required error={errors.opening_officer_id}>
              <select value={shared.opening_officer_id} onChange={setS('opening_officer_id')}
                className={errors.opening_officer_id ? icErr : ic}>
                <option value="">-- තෝරන්න --</option>{empOptions}
              </select>
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="පරීක්ෂා නිලධාරී 1" required error={errors.inspection_officer_1_id}>
                <select value={shared.inspection_officer_1_id} onChange={setS('inspection_officer_1_id')}
                  className={errors.inspection_officer_1_id ? icErr : ic}>
                  <option value="">-- තෝරන්න --</option>{empOptions}
                </select>
              </Field>
              <Field label="පරීක්ෂා නිලධාරී 2">
                <select value={shared.inspection_officer_2_id} onChange={setS('inspection_officer_2_id')} className={ic}>
                  <option value="">-- නොමැත --</option>{empOptions}
                </select>
              </Field>
            </div>
          </Section>
        )}

        {/* ══ Submit ══ */}
        {shared.type && (
          <div className="flex items-center justify-end gap-4 pt-2">
            <button type="button" onClick={() => navigate('/maintenance')}
              className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition">
              අවලංගු කරන්න
            </button>
            <button type="submit" disabled={saving}
              className="px-8 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-50 transition">
              {saving ? 'සුරකිමින්...' : isEdit ? 'යාවත්කාලීන කරන්න' : isSysUnit ? 'සුරකින්න' : `සුරකින්න (උපකරණ ${devices.length})`}
            </button>
          </div>
        )}

      </form>
    </div>
  );
}